import "./App.css"
import React from "react"
import { BrowserRouter, Routes, Route } from "react-router-dom"
import { AdminLoginPage } from "./pages/admins/AdminLoginPage"
import CssBaseline from "@mui/material/CssBaseline"
import Container from "@mui/material/Container"
import { CreateAppAdminPage } from "./pages/admins/app/CreateAppAdminPage"
import { AuthProvider } from "react-auth-kit"
import { Header } from "./components/Header"
import { CreateEventAdminPage } from "./pages/admins/app/CreateEventAdminPage"
import { CreateEventPage } from "./pages/admins/app/CreateEventPage"
import { ManageAdminsPage } from "./pages/admins/app/ManageAdminsPage"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { AdminDashboardPage } from "./pages/admins/AdminDashboardPage"
import { EditEventPage } from "./pages/admins/app/EditEventPage"
import CreateVotingInstancePage from "./pages/admins/event/instances/CreateVotingInstancePage"
import { SpecificVotingInstancePage } from "./pages/admins/event/instances/SpecificVotingInstancePage"
import { DeleteEventPage } from "./pages/admins/app/DeleteEventPage"
import { VoterLoginPage } from "./pages/voters/VoterLoginPage"
import { VoterDashboardPage } from "./pages/voters/VoterDashboardPage"
import { ManageVoterPage } from "./pages/admins/event/ManageVoterPage"
import { ArchivedInformationPage } from "./pages/admins/app/ArchivedInformationPage"
import { ArchiveEventPage } from "./pages/admins/app/ArchiveEventPage"
import ViewArchivesPage from "./pages/admins/app/ViewArchivesPage"
import AddQuestionPage from "./pages/admins/event/questions/AddQuestionPage"
import EditQuestionPage from "./pages/admins/event/questions/EditQuestionPage"
import DeleteQuestionsPage from "./pages/admins/event/questions/DeleteQuestionsPage"
import DeleteInstancePage from "./pages/admins/event/instances/DeleteInstancePage"
import EventStartPage from "./pages/admins/event/EventStartPage"
import StartInstancePage from "./pages/admins/event/instances/StartInstancePage"
import EditInstancePage from "./pages/admins/event/instances/EditInstancePage"
import LoadQuestionsPage from "./pages/voters/LoadQuestionsPage"
import VoterResultsPage from "./pages/voters/VoterResultsPage"
import InstanceResultsPage from "./pages/admins/event/instances/InstanceResultsPage"
import LobbyPage from "./pages/voters/LobbyPage"
import HomePage from "./pages/HomePage"
import ForbiddenPage from "./pages/ForbiddenPage"
import NotFoundPage from "./pages/NotFoundPage"
import ProtectedRoute from "./components/ProtectedRoute"
import LiveInstancePage from "./pages/admins/event/instances/LiveInstancePage"
import { QueryClient, QueryClientProvider } from "react-query"
import InstanceVotersPage from "./pages/admins/event/instances/InstanceVotersPage"

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#6573C3",
    },
    secondary: {
      main: "#5E437C",
    },
    error: {
      main: "#f44336",
    },
    warning: {
      main: "#ff9800",
    },
    info: {
      main: "#2196f3",
    },
    success: {
      main: "#4caf50",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0bec5",
      disabled: "#757575",
    },
  },
  typography: {
    fontFamily: '"Rubik", "Poppins", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 4,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "html, body, #root": {
          height: "100%",
        },
      },
    },
  },
  transitions: {
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    },
    duration: {
      shortest: 150,
    },
  },
})

const queryClient = new QueryClient()

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        <ThemeProvider theme={darkTheme}>
          <CssBaseline />
          <AuthProvider
            authType="cookie"
            authName="auth_cookies"
            cookieDomain={window.location.hostname}
            cookieSecure={false}
          >
            <BrowserRouter>
              <Header />
              <Container>
                <Routes>
                  {/* General Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/not-authorized" element={<ForbiddenPage />} />
                  <Route path="*" element={<NotFoundPage />} />

                  {/* Voter Routes */}
                  <Route
                    path="/voter/dashboard"
                    element={
                      <ProtectedRoute userType="voter">
                        <VoterDashboardPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/voter/instance/:id/lobby"
                    element={
                      <ProtectedRoute userType="voter">
                        <LobbyPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/voter/instance/:id/questions"
                    element={
                      <ProtectedRoute userType="voter">
                        <LoadQuestionsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/voter/instance/:id/results"
                    element={
                      <ProtectedRoute userType="voter">
                        <VoterResultsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/voter/login" element={<VoterLoginPage />} />

                  {/* Admin Routes */}
                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute userType="admin">
                        <AdminDashboardPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* App Admin Routes */}
                  <Route
                    path="/admin/app-admin/manage-admins"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <ManageAdminsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/create"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <CreateAppAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/create-event-admin"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <CreateEventAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/event/create"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <CreateEventPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/event/edit"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <EditEventPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/event/delete"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <DeleteEventPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/event/archive"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <ArchiveEventPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/app-admin/event/view-archives"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <ViewArchivesPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/app-admin/event/:id/archived-information"
                    element={
                      <ProtectedRoute userType="appAdmin">
                        <ArchivedInformationPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Event Admin Routes */}
                  <Route
                    path="/admin/event-admin/event/:id/start"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <EventStartPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/event/instance-start"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <StartInstancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/:id/results"
                    element={
                      <ProtectedRoute userType="admin">
                        <InstanceResultsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* Manage Voters for event admin */}
                  <Route
                    path="/admin/event-admin/manage-voters"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <ManageVoterPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/:id/live"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <LiveInstancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/create"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <CreateVotingInstancePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/event-admin/instance/edit"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <EditInstancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/delete"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <DeleteInstancePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/event-admin/instance/:id"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <SpecificVotingInstancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/:id/add-question"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <AddQuestionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/:id/questions/edit"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <EditQuestionPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/:id/questions/delete"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <DeleteQuestionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/event-admin/instance/:instanceId/voters"
                    element={
                      <ProtectedRoute userType="eventAdmin">
                        <InstanceVotersPage />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Container>
            </BrowserRouter>
          </AuthProvider>
        </ThemeProvider>
      </div>
    </QueryClientProvider>
  )
}

export default App
